/**
 * Idle prefetch for lazy routes (W11.5 P1 — nav snappiness).
 *
 * After login the shell sits idle while the officer reads the page in
 * front of them; that idle time pays for every later navigation. The
 * import specifiers here MUST match App.tsx's lazy() calls exactly —
 * Vite resolves both to the same chunk, so React.lazy finds the module
 * already in cache and the route renders without a spinner.
 *
 * Fire-and-forget: failures are swallowed (navigation falls back to the
 * normal lazy fetch), and imports are staggered so they never compete
 * with the page's own data loads.
 */

const ROUTE_IMPORTS: Array<() => Promise<unknown>> = [
  () => import('./components/simulation/ScenarioSelect'),
  () => import('./components/simulation/SimulationView'),
  () => import('./components/simulation/SessionHistory'),
  () => import('./components/simulation/DebriefView'),
  () => import('./components/training/TrainingHub'),
  () => import('./components/department/DepartmentView'),
  () => import('./components/insights/InsightsDashboard'),
  () => import('./components/community/CommunityConnect'),
  () => import('./components/settings/SettingsPage'),
];

let started = false;

export function prefetchAppRoutes(): void {
  if (started) return;
  started = true;

  const idle = (fn: () => void) =>
    'requestIdleCallback' in window
      ? (window as Window & { requestIdleCallback: (cb: () => void, opts?: { timeout: number }) => void })
          .requestIdleCallback(fn, { timeout: 2500 })
      : setTimeout(fn, 800);

  idle(() => {
    ROUTE_IMPORTS.forEach((load, i) => {
      setTimeout(() => { load().catch(() => undefined); }, i * 150);
    });
  });
}
